/**
 * Item definitions and instance generation — pure logic.
 *
 * Every droppable equipment becomes a unique INSTANCE with a uuid, a rarity,
 * an item level, base stats from its type, and rolled affixes. The uuid is what
 * makes inventories and (later) trading coherent — never skip it.
 */

/** Generate a unique id for an item instance. */
export function newItemId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for environments without crypto.randomUUID.
  return 'it_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Pick a weighted key from a {key: weight} map.
 * @param {Record<string, number>} weights
 * @param {import('./rng.js').Rng} rng
 * @returns {string}
 */
export function weightedPick(weights, rng) {
  const entries = Object.entries(weights);
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let roll = rng.next() * total;
  for (const [key, w] of entries) {
    roll -= w;
    if (roll <= 0) return key;
  }
  return entries[entries.length - 1][0];
}

/**
 * Roll a single affix value, scaled by item level and rarity multiplier.
 * @param {object} affixDef from affixes.json
 * @param {number} itemLevel
 * @param {number} rollMult rarity roll multiplier
 * @param {import('./rng.js').Rng} rng
 * @returns {{key:string, stat:string, value:number, isPct:boolean, label:string}}
 */
function rollAffix(key, affixDef, itemLevel, rollMult, rng) {
  const base = rng.range(affixDef.min, affixDef.max);
  const scaled = (base + (affixDef.perLevel ?? 0) * (itemLevel - 1)) * rollMult;
  const value = affixDef.isPct ? Math.round(scaled * 1000) / 1000 : Math.round(scaled);
  const shown = affixDef.isPct ? Math.round(value * 100) : value;
  return {
    key,
    stat: affixDef.stat,
    value,
    isPct: !!affixDef.isPct,
    label: affixDef.label.replace('{v}', String(shown)),
  };
}

/**
 * Generate an equipment instance of a given type and rarity.
 * @param {string} typeId key in items.json
 * @param {string} rarity key in rarity.json
 * @param {number} itemLevel
 * @param {{items:object, rarity:object, affixes:object}} data
 * @param {import('./rng.js').Rng} rng
 * @returns {object} item instance
 */
export function rollEquipment(typeId, rarity, itemLevel, data, rng) {
  const def = data.items[typeId];
  const rDef = data.rarity[rarity];
  const [minAff, maxAff] = rDef.affixes;
  const affixCount = rng.int(minAff, maxAff);

  const pool = Object.keys(data.affixes);
  const chosen = [];
  const used = new Set();
  while (chosen.length < affixCount && used.size < pool.length) {
    // Weighted pick (without replacement): an affix may carry a `weight`
    // (default 1); rare effect affixes (lifesteal/burn/thorns/regen) use a low
    // weight so they stay special instead of showing up on every drop.
    const avail = pool.filter((k) => !used.has(k));
    const total = avail.reduce((s, k) => s + (data.affixes[k].weight ?? 1), 0);
    let r = rng.range(0, total);
    let key = avail[avail.length - 1];
    for (const k of avail) {
      r -= (data.affixes[k].weight ?? 1);
      if (r <= 0) { key = k; break; }
    }
    used.add(key);
    chosen.push(rollAffix(key, data.affixes[key], itemLevel, rDef.rollMult, rng));
  }

  return {
    id: newItemId(),
    kind: 'equipment',
    typeId,
    slot: def.slot,
    weapon: def.weapon,           // weapon TYPE (sword/dagger/bow/staff/wand) for class-lock
    classes: def.classes,         // optional explicit class allow-list
    icon: def.icon,
    label: def.label,
    rarity,
    itemLevel,
    baseStats: { ...def.baseStats },
    affixes: chosen,
  };
}

/**
 * Sum an equipment instance's total stat contributions (base + affixes).
 * @param {object} item equipment instance
 * @returns {Record<string, number>}
 */
export function equipmentStatTotals(item) {
  /** @type {Record<string, number>} */
  const totals = { ...item.baseStats };
  for (const a of item.affixes) {
    totals[a.stat] = (totals[a.stat] ?? 0) + a.value;
  }
  return totals;
}
